import React, { useState } from 'react';
import { Button, Popover, Segmented, Select } from 'antd';

import { CHEVRON_DOWN, KEBAB } from '../icons';

const DENSITY_OPTIONS = [
	{ value: 'comfortable', label: 'Comfortable' },
	{ value: 'compact', label: 'Compact' },
];

/**
 * DISPLAY ▾ on the items page (KPR-1633): how the list is drawn, gathered from
 * the toolbar into one menu. Each setting is a labelled row:
 *
 *   Group          the period separators, with Collapse all / Expand all
 *   Columns        Columns… (and the sub-collection column) for a table of one card
 *   Tile           Arrange the tile, for a grid (or board) of one card
 *   Row density    Comfortable / Compact
 *
 * A setting that does not apply here stays in its place, disabled, with the
 * reason written under it (readable without hover, platform-standards §3).
 *
 * On a phone the trigger is the toolbar's ⋯ and the menu leads with `lead`
 * (the view switch), since the phone's third row has room for one button.
 */
export default function ItemsDisplayMenu({
	isMobile = false,
	lead = null,
	group,
	columnsButton = null,
	collectionColumnButton = null,
	tileLayoutButton = null,
	viewMode,
	density,
	onDensityChange,
}) {
	const [open, setOpen] = useState(false);
	const content = (
		<div className="items-display" data-items-display data-testid="items-display">
			{lead ? <div className="items-display__lead">{lead}</div> : null}
			{group ? <GroupRow {...group} /> : null}
			<DisplayRow
				label="Columns"
				reason={columnsButton ? null : viewMode === 'table'
					? 'Choose one card to set its table columns.'
					: 'Switch to the table to choose its columns.'}
			>
				{columnsButton || <Button disabled>Columns…</Button>}
				{collectionColumnButton}
			</DisplayRow>
			<DisplayRow
				label="Tile"
				reason={tileLayoutButton ? null : viewMode === 'grid' || viewMode === 'board'
					? 'Choose one card to arrange its tile.'
					: 'Switch to the grid to arrange the tile.'}
			>
				{tileLayoutButton || <Button disabled>Arrange tile…</Button>}
			</DisplayRow>
			<DisplayRow label="Row density" id="items-display-density">
				<Segmented
					aria-labelledby="items-display-density"
					options={DENSITY_OPTIONS}
					value={density}
					onChange={onDensityChange}
				/>
			</DisplayRow>
		</div>
	);
	return (
		<Popover
			trigger="click"
			placement="bottomRight"
			open={open}
			onOpenChange={setOpen}
			content={content}
			overlayClassName="items-display-popover"
		>
			{isMobile ? (
				<Button className="items-page__display items-page__display--more" icon={<KEBAB />} aria-label="View and display" aria-expanded={open} />
			) : (
				<Button className="items-page__display" aria-expanded={open} data-toolbar-display>
					Display <CHEVRON_DOWN aria-hidden="true" className="items-page__display-caret" />
				</Button>
			)}
		</Popover>
	);
}

function DisplayRow({ label, id, reason = null, children }) {
	return (
		<div className="items-display__row">
			<div className="items-display__label" id={id}>{label}</div>
			<div className="items-display__control">{children}</div>
			{reason ? <div className="items-display__reason">{reason}</div> : null}
		</div>
	);
}

// "Group": the period separators. It goes quiet (disabled, with the reason)
// wherever separators cannot apply; Collapse all / Expand all fold the groups.
function GroupRow({ value, options, onChange, disabledReason = null, onCollapseAll }) {
	return (
		<DisplayRow label="Group" id="items-display-group" reason={disabledReason}>
			<Select
				className="items-page__group"
				value={value}
				disabled={Boolean(disabledReason)}
				onChange={onChange}
				options={options}
				popupMatchSelectWidth={false}
				classNames={{ popup: { root: 'items-page__group-popup' } }}
				aria-label="Group items"
			/>
			{disabledReason ? null : (
				<span className="items-display__group-fold">
					<Button type="link" size="small" onClick={() => onCollapseAll(true)}>Collapse all</Button>
					<span aria-hidden="true">·</span>
					<Button type="link" size="small" onClick={() => onCollapseAll(false)}>Expand all</Button>
				</span>
			)}
		</DisplayRow>
	);
}

